import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Layers, TrendingUp, AlertCircle, MapPin, DollarSign, FileText, BarChart3 } from "lucide-react";
import InvoicesReport from "./InvoicesReport";

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, accent = "bg-slate-900" }) => (
  <Card className="p-5 flex items-start gap-4">
    <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <div className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  </Card>
);

function SummaryReport() {
  const { t } = useI18n();
  const [data, setData] = useState({ sedes: [], totals: {}, by_department: [], unattributed: {} });

  useEffect(() => {
    api.get("/reports/by-sede").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const maxRevenue = useMemo(
    () => Math.max(1, ...data.sedes.map((s) => s.paid_total + s.pending_total)),
    [data.sedes]
  );

  const totals = data.totals || {};
  const una = data.unattributed || {};

  return (
    <div>
      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <Stat icon={Users} label={t("students")} value={totals.students ?? 0} accent="bg-blue-600" />
        <Stat icon={Layers} label={t("groups")} value={totals.groups ?? 0} accent="bg-indigo-600" />
        <Stat icon={DollarSign} label={t("paid_total")} value={fmt(totals.paid_total)} accent="bg-green-600" />
        <Stat icon={TrendingUp} label={t("paid_last_30d")} value={fmt(totals.paid_last_30d)} accent="bg-emerald-600" />
      </div>

      {/* Por departamento (Español / Inglés) con la suma final de ambos */}
      {(data.by_department || []).length > 1 && (
        <div className="mb-8" data-testid="report-by-department">
          <h3 className="font-display text-xl font-semibold mb-3">Por departamento</h3>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Departamento</TableHead>
                  <TableHead className="text-right">{t("students")}</TableHead>
                  <TableHead className="text-right">{t("groups")}</TableHead>
                  <TableHead className="text-right">{t("paid_total")}</TableHead>
                  <TableHead className="text-right">{t("paid_last_30d")}</TableHead>
                  <TableHead className="text-right">{t("pending_total")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.by_department.map((d) => (
                  <TableRow key={d.department_id} data-testid={`report-dept-${d.department_id}`}>
                    <TableCell className="font-medium">{d.department_name}</TableCell>
                    <TableCell className="text-right font-mono">{d.students}</TableCell>
                    <TableCell className="text-right font-mono">{d.groups}</TableCell>
                    <TableCell className="text-right font-mono text-green-700 font-semibold">{fmt(d.paid_total)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(d.paid_last_30d)}</TableCell>
                    <TableCell className="text-right font-mono text-orange-600">{fmt(d.pending_total)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50 font-semibold" data-testid="report-dept-total">
                  <TableCell>Total ambos</TableCell>
                  <TableCell className="text-right font-mono">{totals.students ?? 0}</TableCell>
                  <TableCell className="text-right font-mono">{totals.groups ?? 0}</TableCell>
                  <TableCell className="text-right font-mono text-green-700">{fmt(totals.paid_total)}</TableCell>
                  <TableCell className="text-right font-mono">{fmt(totals.paid_last_30d)}</TableCell>
                  <TableCell className="text-right font-mono text-orange-600">{fmt(totals.pending_total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Per-sede table */}
      <h3 className="font-display text-xl font-semibold mb-3">{t("by_sede")}</h3>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-6">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("sede")}</TableHead>
              <TableHead>Depto.</TableHead>
              <TableHead className="text-right">{t("students")}</TableHead>
              <TableHead className="text-right">{t("groups")}</TableHead>
              <TableHead className="text-right">{t("paid_total")}</TableHead>
              <TableHead className="text-right">{t("paid_last_30d")}</TableHead>
              <TableHead className="text-right">{t("pending_total")}</TableHead>
              <TableHead className="text-right">{t("payments_paid_count")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.sedes.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {data.sedes.map((s) => (
              <TableRow key={s.sede_id} data-testid={`report-sede-${s.sede_id}`}>
                <TableCell>
                  <div className="font-medium flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                    {s.sede_name}
                  </div>
                  {s.address && <div className="text-xs text-slate-500 ml-5">{s.address}</div>}
                </TableCell>
                <TableCell className="text-xs text-slate-600">{s.department_name}</TableCell>
                <TableCell className="text-right font-mono">{s.students}</TableCell>
                <TableCell className="text-right font-mono">{s.groups}</TableCell>
                <TableCell className="text-right font-mono text-green-700 font-semibold">{fmt(s.paid_total)}</TableCell>
                <TableCell className="text-right font-mono">{fmt(s.paid_last_30d)}</TableCell>
                <TableCell className="text-right font-mono text-orange-600">{fmt(s.pending_total)}</TableCell>
                <TableCell className="text-right font-mono">{s.paid_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Horizontal bar chart of revenue by sede */}
      {data.sedes.length > 0 && (
        <>
          <h3 className="font-display text-xl font-semibold mb-3">{t("revenue_chart")}</h3>
          <Card className="p-6">
            <div className="space-y-4">
              {data.sedes.map((s) => {
                const paidPct = (s.paid_total / maxRevenue) * 100;
                const pendingPct = (s.pending_total / maxRevenue) * 100;
                return (
                  <div key={s.sede_id} data-testid={`chart-row-${s.sede_id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-slate-900">{s.sede_name}</span>
                      <span className="text-xs text-slate-500 font-mono">
                        <span className="text-green-700 font-semibold">{fmt(s.paid_total)}</span>
                        {s.pending_total > 0 && <span className="text-orange-600"> · {fmt(s.pending_total)} {t("pending").toLowerCase()}</span>}
                      </span>
                    </div>
                    <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                      <div
                        className="bg-green-500 transition-all"
                        style={{ width: `${paidPct}%` }}
                        title={`${t("paid_total")}: ${fmt(s.paid_total)}`}
                      />
                      <div
                        className="bg-orange-400 transition-all"
                        style={{ width: `${pendingPct}%` }}
                        title={`${t("pending_total")}: ${fmt(s.pending_total)}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-5 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 bg-green-500 rounded-sm" /> {t("paid")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-3 w-3 bg-orange-400 rounded-sm" /> {t("pending")}
              </span>
            </div>
          </Card>
        </>
      )}

      {/* Unattributed warning */}
      {(una.paid_total > 0 || una.pending_total > 0) && (
        <Card className="mt-6 p-5 border-orange-200 bg-orange-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-orange-900">{t("unattributed_title")}</div>
              <div className="text-sm text-orange-800 mt-1">
                {t("unattributed_desc")} {fmt(una.paid_total)} {t("paid").toLowerCase()} · {fmt(una.pending_total)} {t("pending").toLowerCase()}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

export default function Reports() {
  const { t } = useI18n();

  return (
    <div>
      <PageHeader
        title={t("reports")}
        subtitle={t("reports_desc")}
        testId="reports-header"
      />

      <Tabs defaultValue="summary" className="mb-4">
        <TabsList className="h-10">
          <TabsTrigger value="summary" data-testid="reports-tab-summary" className="gap-2">
            <BarChart3 className="h-4 w-4" /> Resumen
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="reports-tab-invoices" className="gap-2">
            <FileText className="h-4 w-4" /> Facturas
          </TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="mt-6">
          <SummaryReport />
        </TabsContent>
        <TabsContent value="invoices" className="mt-6">
          <InvoicesReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
